/**
 * Minesweeper Board Component
 *
 * Grid of cells with classic minesweeper styling:
 * - Unrevealed: raised 3D button look
 * - Revealed: flat with number (colored by count) or mine
 * - Flagged: 🚩 on raised background
 * - Mine (game over): 💣, red background for the death cell
 * - Wrong flag (game over): 🚩 with ❌
 *
 * Supports right-click flagging and long-press for mobile.
 */

import React, { useCallback, useRef } from 'react';
import {
  MinesweeperBoardProps,
  MinesweeperCell,
  NUMBER_COLORS,
} from '../types';
import './MinesweeperBoard.css';

const MinesweeperBoard: React.FC<MinesweeperBoardProps> = ({
  board,
  gameStatus,
  deathCell,
  onCellClick,
  onCellRightClick,
  difficulty,
}) => {
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, row: number, col: number) => {
      e.preventDefault();
      e.stopPropagation();
      onCellRightClick(row, col);
    },
    [onCellRightClick]
  );

  const handleClick = useCallback(
    (row: number, col: number) => {
      if (longPressTriggeredRef.current) {
        longPressTriggeredRef.current = false;
        return;
      }
      onCellClick(row, col);
    },
    [onCellClick]
  );

  // Long-press support for mobile flagging
  const handleTouchStart = useCallback(
    (row: number, col: number) => {
      longPressTriggeredRef.current = false;
      longPressTimerRef.current = setTimeout(() => {
        longPressTriggeredRef.current = true;
        onCellRightClick(row, col);
      }, 500);
    },
    [onCellRightClick]
  );

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const isDeathCell = useCallback(
    (row: number, col: number) =>
      deathCell !== null && deathCell.row === row && deathCell.col === col,
    [deathCell]
  );

  const isWrongFlag = useCallback(
    (cell: MinesweeperCell) =>
      gameStatus === 'lost' && cell.isFlagged && !cell.isMine,
    [gameStatus]
  );

  const getCellClassName = useCallback(
    (cell: MinesweeperCell, row: number, col: number) => {
      const classes = ['ms-board__cell'];

      if (cell.isRevealed) {
        classes.push('ms-board__cell--revealed');
        if (cell.isMine) {
          classes.push('ms-board__cell--mine');
          if (isDeathCell(row, col)) {
            classes.push('ms-board__cell--death');
          }
        } else if (cell.adjacentMines > 0) {
          classes.push(`ms-board__cell--num-${cell.adjacentMines}`);
        }
      } else {
        classes.push('ms-board__cell--hidden');
        if (cell.isFlagged) {
          classes.push('ms-board__cell--flagged');
        }
      }

      if (isWrongFlag(cell)) {
        classes.push('ms-board__cell--wrong-flag');
      }

      return classes.join(' ');
    },
    [isDeathCell, isWrongFlag]
  );

  const renderCellContent = useCallback(
    (cell: MinesweeperCell, row: number, col: number) => {
      // Game over: show wrong flags
      if (isWrongFlag(cell)) {
        return (
          <span className="ms-board__cell-content">
            <span className="ms-board__flag">🚩</span>
            <span className="ms-board__wrong-x">❌</span>
          </span>
        );
      }

      // Flagged (not revealed)
      if (cell.isFlagged && !cell.isRevealed) {
        return <span className="ms-board__cell-content">🚩</span>;
      }

      // Not revealed
      if (!cell.isRevealed) {
        return null;
      }

      // Mine
      if (cell.isMine) {
        return <span className="ms-board__cell-content">💣</span>;
      }

      // Number
      if (cell.adjacentMines > 0) {
        return (
          <span
            className="ms-board__cell-content ms-board__number"
            style={{ color: NUMBER_COLORS[cell.adjacentMines] }}
          >
            {cell.adjacentMines}
          </span>
        );
      }

      // Empty revealed cell
      return null;
    },
    [isWrongFlag]
  );

  const isGameOver = gameStatus === 'won' || gameStatus === 'lost';

  return (
    <div
      className={`ms-board ms-board--${difficulty}`}
      role="grid"
      aria-label="Minesweeper board"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="ms-board__grid">
        {board.map((row, rowIdx) => (
          <div key={rowIdx} className="ms-board__row" role="row">
            {row.map((cell, colIdx) => (
              <button
                key={`${rowIdx}-${colIdx}`}
                className={getCellClassName(cell, rowIdx, colIdx)}
                onClick={() => handleClick(rowIdx, colIdx)}
                onContextMenu={(e) => handleContextMenu(e, rowIdx, colIdx)}
                onTouchStart={() => handleTouchStart(rowIdx, colIdx)}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchMove}
                disabled={isGameOver}
                role="gridcell"
                aria-label={getCellAriaLabel(cell, rowIdx, colIdx, isDeathCell(rowIdx, colIdx))}
                tabIndex={-1}
              >
                {renderCellContent(cell, rowIdx, colIdx)}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

function getCellAriaLabel(
  cell: MinesweeperCell,
  row: number,
  col: number,
  isDeath: boolean
): string {
  const pos = `Row ${row + 1}, Column ${col + 1}`;
  if (cell.isFlagged && !cell.isRevealed) return `${pos}, flagged`;
  if (!cell.isRevealed) return `${pos}, hidden`;
  if (cell.isMine) return `${pos}, mine${isDeath ? ' (clicked)' : ''}`;
  if (cell.adjacentMines > 0) return `${pos}, ${cell.adjacentMines} adjacent mines`;
  return `${pos}, empty`;
}

export { MinesweeperBoard };
export default MinesweeperBoard;
